import React, { useState,useEffect,useRef } from 'react';
import './Faq.css'; // Import the specific CSS

// Reusable Intersection Observer Hook (Assume available)
const useVisibility = (options) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const elementRef = React.useRef(null);
    const observerRef = React.useRef(null);

    React.useEffect(() => {
        const currentElement = elementRef.current;
        if (!currentElement) return;
        observerRef.current = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    if (options?.triggerOnce && observerRef.current) {
                        observerRef.current.unobserve(currentElement);
                    }
                } else {
                    if (options && !options.triggerOnce) {
                        setIsVisible(false);
                    }
                }
            });
        }, options);
        observerRef.current.observe(currentElement);
        return () => { if (observerRef.current && currentElement) observerRef.current.unobserve(currentElement); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options?.threshold, options?.rootMargin, options?.triggerOnce]);

    return [elementRef, isVisible];
};

// Placeholder Icon for Accordion Toggle (Replace with animated SVG if desired)
const AccordionIcon = ({ isOpen }) => (
    <svg className={`faq-accordion-icon ${isOpen ? 'is-open' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 0V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M0 8H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="faq-icon-horizontal"/>
    </svg>
);


// FAQ Data
const faqData = [
    {
        id: 1,
        question: "What services does Cliniqor Healthcare Project & Manpower Solutions offer?",
        answer: "We provide end-to-end healthcare solutions including medical manpower recruitment, hospital and college infrastructure setup, hostel management with AMC services, and medical equipment trade (buy/sell)."
    },
    {
        id: 2,
        question: "Which medical profiles do you recruit for?",
        answer: "We specialize in recruiting top-tier professionals such as Dermatologists, Cardiologists, Anesthesiologists, Physicians, Emergency Doctors, Neurosurgeons, Paediatricians, Urologists, Gastroenterologists, and more across all key specialties."
    },
    {
        id: 3,
        question: "Are your recruitment services available pan-India?",
        answer: "Yes, we serve hospitals, clinics, and medical institutions across India through our strategically located offices in Dehradun, Goa ,Chandigarh, West Bengal ,Pune, Delhi, Mumbai , Hydrabad and Bengaluru ."
    },
    {
        id: 4,
        question: "How do you ensure the quality of candidates?",
        answer: "All candidates undergo a rigorous screening, evaluation, and verification process by our experienced recruitment associates who specialize in healthcare placements."
    },
    {
        id: 5,
        question: "Do you provide placement services for fresh medical graduates or only experienced professionals?",
        answer: "Yes, we place both freshers and experienced professionals. Our team evaluates candidate potential beyond experience, offering opportunities that align with career aspirations."
    },
    {
        id: 6,
        question: "Can you assist with setting up medical colleges or hospital infrastructure?",
        answer: "Yes, we offer complete infrastructure consulting for colleges and hospitals, including architectural planning, equipment procurement, and hostel facility development with ongoing AMC support."
    },
     {
        id: 7,
        question: "How quickly can you fulfill manpower requirements?",
        answer: "With a vast network of qualified medical professionals and an agile recruitment process, we pride ourselves on delivering ideal candidates in the shortest possible timeframe."
    },
     {
        id: 8,
        question: "Do you offer support for medical equipment purchase or resale?",
        answer: "Yes. We provide a secure, transparent platform for hospitals to buy or sell high-quality medical equipment, eliminating middlemen and ensuring fair trade."
    },
     
    {
        id: 9,
        question: "How can I get started with your services?",
        answer: "You can reach out to us through our website contact form or directly visit one of our offices in Dehradun,Goa, West Bengal, Pune, Delhi, Mumbai ,Chandigarh, Hydrabad and Bengaluru. Our expert consultants will guide you through every step of the onboarding process."
    },
];


// Individual FAQ Item Component
function FaqItem({ item, index, openIndex, setOpenIndex }) {
    const contentRef = useRef(null);
    const isOpen = index === openIndex;

    const toggleItem = () => {
        setOpenIndex(isOpen ? null : index); // Set to null to close, or index to open
    };

    // Calculate content height dynamically
    const [contentHeight, setContentHeight] = useState(0);
    useEffect(() => {
        if (isOpen && contentRef.current) {
            setContentHeight(contentRef.current.scrollHeight);
        } else {
            setContentHeight(0);
        }
        // Optional: Recalculate on resize if needed
        // const handleResize = () => { ... }; window.addEventListener('resize', handleResize); return () => window.removeEventListener(...);
    }, [isOpen]);


    return (
        <div className={`faq-item ${isOpen ? 'is-open' : ''}`}>
            <button
                className="faq-question-button"
                onClick={toggleItem}
                aria-expanded={isOpen}
                aria-controls={`faq-answer-${item.id}`}
            >
                <span className="faq-question-number">{(index + 1).toString().padStart(2, '0')}</span>
                <span className="faq-question-text">{item.question}</span>
                <div className="faq-icon-wrapper">
                    <AccordionIcon isOpen={isOpen} />
                </div>
            </button>
            <div
                id={`faq-answer-${item.id}`}
                className="faq-answer-content"
                style={{ maxHeight: `${contentHeight}px` }} // Animate max-height
                aria-hidden={!isOpen}
            >
                <div ref={contentRef} className="faq-answer-inner">
                    <p>{item.answer}</p>
                </div>
            </div>
        </div>
    );
}


// Main FAQ Page Component
function FaqPage() {
    const [headerRef, isHeaderVisible] = useVisibility({ threshold: 0.3, triggerOnce: true });
    const [faqListRef, isFaqListVisible] = useVisibility({ threshold: 0.1, triggerOnce: true });
    const [openIndex, setOpenIndex] = useState(null); // State to track which item is open

    return (
        <div className="faq-page-container">

            {/* ==== Page Header ==== */}
            <header ref={headerRef} className={`faq-page-header ${isHeaderVisible ? 'is-visible' : ''}`}>
                 {/* Background Elements */}
                <div className="faq-header-bg-blur blob-1"></div>
                <div className="faq-header-bg-blur blob-2"></div>
                <div className="faq-header-bg-lines"></div>

                <div className="faq-page-header-content">
                     {/* Animated Title */}
                    <h1 className="faq-page-main-title">
                        <span className="faq-title-line line-1">Frequently Asked</span>
                        <span className="faq-title-line line-3">Questions</span>
                    </h1>
                    <p className="faq-page-subtitle">
                        Your questions, answered. Find quick solutions and insights into our services.
                    </p>
                </div>
            </header>

            {/* ==== FAQ Accordion Section ==== */}
            <section ref={faqListRef} className={`faq-list-section ${isFaqListVisible ? 'is-visible' : ''}`}>
                <div className="faq-list-container">
                    {faqData.map((item, index) => (
                        <FaqItem
                            key={item.id}
                            item={item}
                            index={index}
                            openIndex={openIndex}
                            setOpenIndex={setOpenIndex}
                        />
                    ))}
                </div>
            </section>

             {/* Optional: Contact prompt if question not answered */}
             <section className="faq-contact-prompt">
                <div className="faq-prompt-container">
                    <h3>Still Have Questions?</h3>
                    <p>Our team is ready to provide the answers you need. Get in touch for personalized assistance.</p>
                    <a href="/contact" className="faq-prompt-button">Contact Support</a>
                </div>
             </section>

        </div> // End faq-page-container
    );
}

export default FaqPage;